import { z } from 'zod'

/**
 * Project inbox (PLAN-084): mail sent to `prj.<projectId>@<domain>` lands in
 * the project's inbox; rules hand matching mail to the agent with a prompt;
 * every run is a chat thread. Shapes shared by the ProjectStore, the
 * procedures, the agent tools and the SPA.
 */

export const inboxFolderSchema = z.enum(['inbox', 'processed', 'archive'])
export type InboxFolder = z.infer<typeof inboxFolderSchema>

export const inboxEmailStatusSchema = z.enum([
  'new',
  'no_match',
  'matched',
  'processing',
  'done',
  'failed',
])
export type InboxEmailStatus = z.infer<typeof inboxEmailStatusSchema>

/** Inbox emails that still need eyes (the sidebar badge). */
export const INBOX_ATTENTION_STATUSES: readonly InboxEmailStatus[] = ['new', 'no_match', 'failed']

export const inboxAttachmentSchema = z.object({
  /** Position in the parsed MIME — the download route and tools address it. */
  index: z.number().int().nonnegative(),
  filename: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  /** Set once the attachment has been filed as a project document (save once). */
  documentId: z.string().nullable().optional(),
})
export type InboxAttachment = z.infer<typeof inboxAttachmentSchema>

export const inboxEmailSchema = z.object({
  id: z.string(),
  messageId: z.string().nullable(),
  fromAddress: z.string(),
  fromName: z.string().nullable(),
  toAddresses: z.array(z.string()),
  ccAddresses: z.array(z.string()),
  subject: z.string().nullable(),
  snippet: z.string(),
  hasAttachments: z.boolean(),
  attachments: z.array(inboxAttachmentSchema),
  folder: inboxFolderSchema,
  status: inboxEmailStatusSchema,
  matchedRuleId: z.string().nullable(),
  /** Chat thread of the latest run, if any. */
  threadId: z.string().nullable(),
  errorMessage: z.string().nullable(),
  receivedAt: z.string(),
  processedAt: z.string().nullable(),
})
export type InboxEmail = z.infer<typeof inboxEmailSchema>

/** Full email: the list shape plus the text body. */
export const inboxEmailDetailSchema = inboxEmailSchema.extend({
  textBody: z.string(),
})
export type InboxEmailDetail = z.infer<typeof inboxEmailDetailSchema>

// ── Rules ─────────────────────────────────────────────────────────────────

export const inboxConditionSchema = z.discriminatedUnion('field', [
  z.object({
    field: z.literal('from'),
    op: z.enum(['contains', 'equals', 'ends_with']),
    value: z.string().min(1).max(200),
  }),
  z.object({
    field: z.literal('subject'),
    op: z.enum(['contains', 'starts_with', 'equals']),
    value: z.string().min(1).max(200),
  }),
  z.object({
    field: z.literal('body'),
    op: z.literal('contains'),
    value: z.string().min(1).max(200),
  }),
  z.object({
    field: z.literal('to'),
    op: z.literal('contains'),
    value: z.string().min(1).max(200),
  }),
  z.object({ field: z.literal('has_attachment'), op: z.literal('is'), value: z.boolean() }),
  z.object({
    field: z.literal('attachment_name'),
    op: z.enum(['contains', 'ends_with']),
    value: z.string().min(1).max(200),
  }),
])
export type InboxCondition = z.infer<typeof inboxConditionSchema>
export type InboxConditionField = InboxCondition['field']

export const inboxMatchModeSchema = z.enum(['all', 'any'])

/**
 * Whether a successful run of this rule files the email under Processed —
 * the rule's `autoArchive` switch. The single place that reads the flag,
 * so the store (finishInboxRun) and the UI (rule badges) cannot disagree.
 */
export function ruleMovesToProcessed(rule: { autoArchive: boolean }): boolean {
  return rule.autoArchive === true
}
export type InboxMatchMode = z.infer<typeof inboxMatchModeSchema>

export const inboxRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  position: z.number().int(),
  conditions: z.array(inboxConditionSchema),
  matchMode: inboxMatchModeSchema,
  prompt: z.string(),
  autoArchive: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type InboxRule = z.infer<typeof inboxRuleSchema>

export const inboxRuleInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  enabled: z.boolean().default(true),
  conditions: z.array(inboxConditionSchema).min(1).max(10),
  matchMode: inboxMatchModeSchema.default('all'),
  prompt: z.string().trim().min(1).max(4000),
  autoArchive: z.boolean().default(true),
})
export type InboxRuleInput = z.infer<typeof inboxRuleInputSchema>

// ── Runs ──────────────────────────────────────────────────────────────────

export const inboxRunStatusSchema = z.enum(['queued', 'running', 'done', 'failed'])
export type InboxRunStatus = z.infer<typeof inboxRunStatusSchema>

export const inboxRunSchema = z.object({
  id: z.string(),
  emailId: z.string(),
  ruleId: z.string().nullable(),
  ruleName: z.string().nullable(),
  threadId: z.string(),
  /** The assistant message the run's reply streams into — the UI scrolls the bubble to it. */
  assistantMessageId: z.string().nullable(),
  prompt: z.string(),
  status: inboxRunStatusSchema,
  /** User the run acted as (the rule's creator / the member who pressed the button). */
  actedAs: z.string().nullable(),
  startedBy: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
})
export type InboxRun = z.infer<typeof inboxRunSchema>

// ── Matching (pure; shared by the worker and the rule editor preview) ────

/** The subset of an email a rule can look at. */
export interface InboxMatchInput {
  fromAddress: string
  fromName: string | null
  toAddresses: string[]
  subject: string | null
  textBody: string
  attachments: Array<{ filename: string }>
}

function norm(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().trim()
}

export function matchCondition(email: InboxMatchInput, condition: InboxCondition): boolean {
  switch (condition.field) {
    case 'from': {
      const needle = norm(condition.value)
      const candidates = [norm(email.fromAddress), norm(email.fromName)].filter(Boolean)
      if (condition.op === 'equals') return candidates.some((c) => c === needle)
      if (condition.op === 'ends_with') return candidates.some((c) => c.endsWith(needle))
      return candidates.some((c) => c.includes(needle))
    }
    case 'subject': {
      const subject = norm(email.subject)
      const needle = norm(condition.value)
      if (condition.op === 'equals') return subject === needle
      if (condition.op === 'starts_with') return subject.startsWith(needle)
      return subject.includes(needle)
    }
    case 'body':
      return norm(email.textBody).includes(norm(condition.value))
    case 'to':
      return email.toAddresses.some((a) => norm(a).includes(norm(condition.value)))
    case 'has_attachment':
      return email.attachments.length > 0 === condition.value
    case 'attachment_name': {
      const needle = norm(condition.value)
      return email.attachments.some((a) =>
        condition.op === 'ends_with'
          ? norm(a.filename).endsWith(needle)
          : norm(a.filename).includes(needle)
      )
    }
  }
}

export function matchRule(
  email: InboxMatchInput,
  rule: Pick<InboxRule, 'conditions' | 'matchMode' | 'enabled'>
): boolean {
  if (!rule.enabled || rule.conditions.length === 0) return false
  const results = rule.conditions.map((c) => matchCondition(email, c))
  return rule.matchMode === 'any' ? results.some(Boolean) : results.every(Boolean)
}

/** First enabled rule (in position order) that matches, or null. */
export function findMatchingRule<
  T extends Pick<InboxRule, 'conditions' | 'matchMode' | 'enabled' | 'position'>,
>(email: InboxMatchInput, rules: T[]): T | null {
  const ordered = [...rules].sort((a, b) => a.position - b.position)
  return ordered.find((r) => matchRule(email, r)) ?? null
}

/** Senders the agent must never reply to, and headers that mark automated mail. */
export const NO_REPLY_ADDRESS_RE =
  /(^|[.\-_])(no-?reply|noreply|do-?not-?reply|mailer-daemon|postmaster|bounce)/i

// ── Previewing attachments in the email view (board card drqs, round 2) ─────

/** Largest attachment the email view previews inline (= the save/read cap). */
export const MAX_PREVIEW_ATTACHMENT_BYTES = 25 * 1024 * 1024

/**
 * Types a browser may render from the attachment route: raster pictures for
 * the thumbnail + lightbox, PDF for the viewer. Everything else (SVG, HTML,
 * HEIC most browsers cannot draw, Office files) stays a download.
 */
const PREVIEW_TYPES: Readonly<Record<string, 'image' | 'pdf'>> = {
  'image/png': 'image',
  'image/jpeg': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/pdf': 'pdf',
}

/** For mail clients that send `application/octet-stream` (or nothing). */
const PREVIEW_EXTENSIONS: Readonly<Record<string, string>> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
}

export interface AttachmentPreview {
  kind: 'image' | 'pdf'
  /** The Content-Type the attachment route serves it inline with. */
  contentType: string
}

/**
 * How the email view can show an attachment, or null when it can only be
 * downloaded. The declared type wins; the extension is only consulted when
 * the sender declared nothing useful, so an `image/svg+xml` named `.png`
 * never renders.
 */
export function attachmentPreview(attachment: {
  filename: string
  mimeType: string
  size?: number
}): AttachmentPreview | null {
  if (attachment.size !== undefined && attachment.size > MAX_PREVIEW_ATTACHMENT_BYTES) return null
  const declared = attachment.mimeType.split(';')[0]!.trim().toLowerCase()
  const generic = !declared || declared === 'application/octet-stream'
  const dot = attachment.filename.lastIndexOf('.')
  const byExtension =
    dot > 0 ? PREVIEW_EXTENSIONS[attachment.filename.slice(dot).toLowerCase()] : undefined
  const contentType = generic ? byExtension : declared === 'image/jpg' ? 'image/jpeg' : declared
  const kind = contentType ? PREVIEW_TYPES[contentType] : undefined
  return kind && contentType ? { kind, contentType } : null
}
